import { Code2, MousePointerClick } from 'lucide-react'
import type { ComponentType } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

// Opaque dynamic import: the editor module is loaded only inside a useEffect
// that fires after `shouldMount` flips, so neither Vite's static analyser nor
// TanStack Start's route manifest emit `<link rel="modulepreload">` hints for
// the CodeMirror or HugeRTE chunks. (`React.lazy` would still get them
// preloaded because the route manifest considers any statically-declared
// dynamic import a candidate for prefetch.)
type DualEditorPanelModule = typeof import('./DualEditorPanel')
type DualEditorPanelComponent = ComponentType<{ initialContent: string }>

const PLACEHOLDER_CLASSES =
  'flex flex-col lg:flex-row gap-5 px-5 py-5 bg-[#dddddd] mb-9'

interface LazyDualEditorPanelProps {
  initialContent: string
}

interface PlaceholderProps {
  onActivate: () => void
  onPrefetch: () => void
}

// Both placeholder tiles are real <button>s so click + keyboard activation
// (Enter/Space) and focus all flow through native semantics. `onPointerEnter`
// + `onFocus` start downloading the editor chunk before the user clicks, so
// when they do the mount feels instant.
function Placeholder({ onActivate, onPrefetch }: PlaceholderProps) {
  const buttonHandlers = {
    onClick: onActivate,
    onPointerEnter: onPrefetch,
    onFocus: onPrefetch
  }
  return (
    <section className={PLACEHOLDER_CLASSES}>
      <div className="flex-1 min-w-0">
        <button
          type="button"
          className="group w-full h-[700px] bg-white rounded border border-gray-300 cursor-pointer flex flex-col items-center justify-center gap-5 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#ff694a]/40"
          {...buttonHandlers}
        >
          <span className="inline-flex items-center justify-center w-20 h-20 rounded-full border-4 border-[#ff694a] text-[#ff694a] transition-transform group-hover:scale-110">
            <MousePointerClick size={36} aria-hidden="true" />
          </span>
          <span className="text-2xl md:text-3xl font-bold font-['Montserrat',sans-serif] text-gray-800">
            Launch editor
          </span>
          <span className="text-base text-gray-500">
            Click to start editing — free WYSIWYG HTML editor
          </span>
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <button
          type="button"
          className="group w-full h-[700px] bg-gray-900 rounded border border-gray-700 cursor-pointer flex flex-col items-center justify-center gap-5 transition-colors hover:bg-gray-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#ff694a]/40"
          {...buttonHandlers}
        >
          <span className="inline-flex items-center justify-center w-20 h-20 rounded-full border-4 border-[#ff694a] text-[#ff694a] transition-transform group-hover:scale-110">
            <Code2 size={36} aria-hidden="true" />
          </span>
          <span className="text-2xl md:text-3xl font-bold font-['Montserrat',sans-serif] text-white">
            View HTML source
          </span>
          <span className="text-base text-gray-400">
            Click to see the generated code
          </span>
        </button>
      </div>
    </section>
  )
}

export function LazyDualEditorPanel({
  initialContent
}: LazyDualEditorPanelProps) {
  // useState(false) / null render identically on SSR and on the client's
  // first render, so React 19 hydration sees matching markup. The effect
  // below only runs post-hydration.
  const [shouldMount, setShouldMount] = useState(false)
  const [Component, setComponent] = useState<DualEditorPanelComponent | null>(
    null
  )

  // Cache the in-flight or resolved import so hover + click share one fetch.
  // On rejection (transient network error, stale hashed asset post-deploy)
  // clear the cache so the next hover/click can retry instead of being stuck
  // with a permanently-rejected promise.
  const importRef = useRef<Promise<DualEditorPanelModule> | null>(null)
  const prefetch = useCallback(() => {
    if (!importRef.current) {
      const p = import('./DualEditorPanel')
      importRef.current = p
      p.catch(() => {
        if (importRef.current === p) importRef.current = null
      })
    }
    return importRef.current
  }, [])

  // Mount: only after explicit user action. Hover already started the fetch,
  // so the await typically resolves immediately. On error, fall back to the
  // placeholder — `prefetch` has already cleared its cache, so a follow-up
  // click will retry the import.
  useEffect(() => {
    if (!shouldMount || Component) return
    let cancelled = false
    prefetch()
      .then((m) => {
        if (!cancelled) setComponent(() => m.DualEditorPanel)
      })
      .catch(() => {
        if (!cancelled) setShouldMount(false)
      })
    return () => {
      cancelled = true
    }
  }, [shouldMount, Component, prefetch])

  if (!Component) {
    return (
      <Placeholder
        onActivate={() => setShouldMount(true)}
        onPrefetch={prefetch}
      />
    )
  }

  return <Component initialContent={initialContent} />
}
